import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputBase, Popover, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { StagedFilterFieldDef, StagedFilterKey } from '../../utils/stagedFilterState';

export interface AddFilterMenuProps {
  /** Fields not already in the bar. */
  fields: StagedFilterFieldDef[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Picking a field adds its pill and opens its value menu — one gesture, not two. */
  onAdd: (key: StagedFilterKey) => void;
}

/**
 * The single affordance for every filter that is not currently applied. Keeps
 * the bar a readout of the query instead of the schema.
 */
const LIST_ID = 'staged-add-filter-list';
const optionId = (index: number) => `${LIST_ID}-option-${index}`;

export default function AddFilterMenu({ fields, open, onOpen, onClose, onAdd }: AddFilterMenuProps) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter((f) => f.label.toLowerCase().includes(q));
  }, [fields, query]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (visible.length === 0) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((prev) => (prev + delta + visible.length) % visible.length);
    } else if (event.key === 'Enter') {
      const field = visible[activeIndex];
      if (field) {
        event.preventDefault();
        onAdd(field.key);
      }
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        onClick={onOpen}
        disabled={fields.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: 34,
          px: 1.375,
          borderRadius: 1.75,
          border: '1px dashed',
          borderColor: open ? 'primary.main' : 'grey.300',
          bgcolor: 'background.paper',
          color: open ? 'primary.dark' : 'text.secondary',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: '0.8125rem',
          '&:hover': { borderColor: 'primary.main', color: 'primary.dark' },
          '&:disabled': { cursor: 'default', opacity: 0.5, borderColor: 'grey.300', color: 'text.disabled' },
        }}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        <span>Add filter</span>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            onKeyDown: handleKeyDown,
            sx: {
              mt: 0.75,
              width: 270,
              p: 0.75,
              borderRadius: 2.25,
              boxShadow: '0 14px 36px rgba(15, 23, 42, 0.15)',
            },
          },
        }}
      >
        <InputBase
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          placeholder="Filter by…"
          inputProps={{
            'aria-label': 'Search filters',
            'aria-controls': LIST_ID,
            // Arrow keys move the highlight while focus stays here, so point
            // assistive tech at the option that Enter would pick.
            'aria-activedescendant': visible[activeIndex] ? optionId(activeIndex) : undefined,
          }}
          sx={{
            width: '100%',
            mb: 0.5,
            px: 1.25,
            height: 32,
            fontSize: '0.8125rem',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            '&.Mui-focused': { borderColor: 'primary.main' },
          }}
        />
        <Box
          id={LIST_ID}
          role="listbox"
          aria-label="Available filters"
          sx={{ display: 'flex', flexDirection: 'column', gap: '1px', maxHeight: 240, overflowY: 'auto' }}
        >
          {visible.map((field, index) => (
            <Box
              key={field.key}
              component="button"
              type="button"
              role="option"
              id={optionId(index)}
              aria-selected={index === activeIndex}
              onClick={() => onAdd(field.key)}
              onMouseEnter={() => setActiveIndex(index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                width: '100%',
                flex: '0 0 auto',
                textAlign: 'left',
                border: 'none',
                borderRadius: 1.5,
                px: 1.125,
                py: 0.875,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.8125rem',
                color: 'text.primary',
                bgcolor: index === activeIndex ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <span>{field.label}</span>
              <Box component="span" sx={{ fontSize: '0.71875rem', color: 'text.disabled' }}>
                {field.group}
              </Box>
            </Box>
          ))}
          {visible.length === 0 && (
            <Typography variant="body2" sx={{ px: 1.125, py: 0.875, color: 'text.disabled' }}>
              No matches
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
